import * as React from 'react';
import { useUpdateOutage } from '@/hooks/api/useOutages';
import { useNotification } from '@/contexts/NotificationContext';
import OutageFormModal from '@/components/outage/OutageFormModal';
import { Typography, Box, IconButton } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { IOutage } from '@/stack-shared/interfaces/model/outage.interface';
import { formatDateHourMinuteSecond } from '@/stack-shared/utils/gen-utils';

interface OutageTableProps {
  type: 'trk' | 'atm' | 'goa' | 'service';
  assetId: number;
  outages: IOutage[];
  onSubmitted?: () => void;
}

function OutageTable({
  type,
  assetId,
  outages,
  onSubmitted,
}: OutageTableProps) {
  const [reportOutageVisible, setReportOutageVisible] = React.useState(false);
  const [initialValues, setInitialValues] = React.useState({});
  const [outageId, setOutageId] = React.useState(0);
  const updateOutageMutation = useUpdateOutage();
  const { showSuccess, showError } = useNotification();

  if (!outages || outages.length === 0) {
    return null; // 👈 No outages, show nothing
  }

  const submitOutage = (values: any) => {
    updateOutageMutation.mutate(
      { ...values, type, assetId, id: outageId },
      {
        onSuccess: () => {
          setReportOutageVisible(false);
          showSuccess('Outage Updated!');
          onSubmitted?.();
        },
        onError: (error: any) => {
          showError(error.message || 'Failed to update outage');
        },
      }
    );
  };

  const columns = [
    {
      accessorKey: 'outageDate',
      header: 'Outage Date',
      Cell: ({ row }: any) => formatDateHourMinuteSecond(row.original.outageDate),
    },
    {
      accessorKey: 'outageReason',
      header: 'Outage Reason',
    },
    {
      accessorKey: 'resolution',
      header: 'Resolution',
    },
    {
      accessorKey: 'resolveDate',
      header: 'Resolve Date',
      Cell: ({ row }: any) => formatDateHourMinuteSecond(row.original.resolveDate),
    },
  ];

  return (
    <>
      <Typography variant="h6" gutterBottom>
        {type.toUpperCase()} Outages
      </Typography>

      <MaterialReactTable
        columns={columns as any}
        data={outages}
        enableTopToolbar={false}
        enableBottomToolbar={false}
        enablePagination={false}
        enableSorting={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enableGlobalFilter={false}
        initialState={{ density: 'compact' }}
        enableRowActions
        positionActionsColumn="last"
        renderRowActions={({ row }) => (
          <Box>
            <IconButton
              onClick={() => {
                setInitialValues(row.original);
                setOutageId(Number(row.original.id));
                setReportOutageVisible(true);
              }}
            >
              <Edit />
            </IconButton>
          </Box>
        )}
      />

      <OutageFormModal
        visible={reportOutageVisible}
        onDismiss={() => setReportOutageVisible(false)}
        onSubmit={submitOutage}
        initialValues={initialValues}
        title={`Update ${type.toUpperCase()} Outage`}
      />
    </>
  );
}

export default OutageTable;
